import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, DEFAULT_CURRENCY_CODE, ErrorHandler, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggleDefaultOptions } from '@angular/material/button-toggle';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MsalBroadcastService, MsalGuard, MsalModule, MsalRedirectComponent, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import dxFrCA from '@vendor/devextreme/localization/messages/fr-CA.json';
import config from 'devextreme/core/config';
import devextremeAjax from 'devextreme/core/utils/ajax';
import { loadMessages } from 'devextreme/localization';
import { DxDateBoxModule, DxNumberBoxModule, DxRadioGroupModule, DxSelectBoxModule, DxTextBoxModule, DxTreeViewModule } from 'devextreme-angular';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { provideLottieOptions } from 'ngx-lottie';
import { localStorageFactory, sessionStorageFactory } from 'ngx-webstorage-service';
import { Userpilot } from 'userpilot';

import { ClientSpecificFieldsModule } from './client-specific-fields/client-specific-fields.module';
import { environment } from '../environments/environment';
import { AccessCodeComponent } from './account/access-code/access-code.component';
import { ForgotPasswordComponent } from './account/account-forgot-password/account-forgot-password.component';
import { AccountResetPasswordComponent } from './account/app-account-reset-password/app-account-reset-password.component';
import { LoginComponent } from './account/login/login.component';
import { PortalComponent } from './account/portal/portal.component';
import { RedirectMappeduserComponent } from './account/redirect-mappeduser/redirect-mappeduser.component';
import { RegisterComponent } from './account/register/register.component';
import { AccountService } from './account/shared';
import { AppComponent } from './app.component';
import { AppRouting } from './app.routes';
import { ApiService, CommonService, IframeService, LoadingSpinnerService, NavigationService, SignalrService } from './common';
import { LocaleInterceptor } from './common/interceptors/locale.interceptor';
import { TokenInterceptor } from './common/interceptors/token.interceptor';
import { CommonListsObservableService } from './common/lists/lists.observable.service';
import { PhoenixCommonModule } from './common/PhoenixCommon.module';
import { NumberToOrdinalPipe } from './common/pipes/number-to-ordinal.pipe';
import { RootService } from './common/root.service';
import { AppSessionStorageService, AppStorageService, appStorageServiceFactory } from './common/services/app-storage.service';
import { AuthService } from './common/services/auth.service';
import { CommonDataService } from './common/services/commonData.service';
import { EventService } from './common/services/event.service';
import { UrlData } from './common/services/urlData.service';
import { UserBehaviorService } from './common/services/user-behavior.service';
import { ConfigurationService } from './configuration/service/configuration.service';
import { GlobalErrorHandler, rollbarFactory, RollbarService } from './global-error-handler';
import { ApplyClassesWhenScrolledIntoViewDirective } from './home/directives/apply-classes-when-scrolled-into-view.directive';
import { HomeComponent } from './home/home.component';
import { appInitializer } from './initialization/app-initializer';
import { LayoutModule } from './layouts/layout.module';
import { ClutterlessTheme, MessageComponentsModule } from './messages/message-components.module';
import { RestrictionModule } from './restriction/restriction.module';
import { sendRequestFactory } from './sendRequestFactory';
import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './sso/msal-config';
import { ViewEmailInBrowserComponent } from './view-email-in-browser/view-email-in-browser.component';

Userpilot.initialize(environment.userPilotToken);

loadMessages(dxFrCA);

const matFormFieldDefaultOptions: MatFormFieldDefaultOptions = {
  appearance: 'outline',
  subscriptSizing: 'dynamic'
};

const matButtonToggleGroupDefaultOptions: MatButtonToggleDefaultOptions = {
  hideSingleSelectionIndicator: true
};

@NgModule({
  declarations: [
    ApplyClassesWhenScrolledIntoViewDirective,
    AppComponent,
    LoginComponent,
    ForgotPasswordComponent,
    RegisterComponent,
    AccountResetPasswordComponent,
    HomeComponent,
    ViewEmailInBrowserComponent,
    AccessCodeComponent,
    RedirectMappeduserComponent,
    PortalComponent
  ],
  imports: [
    PhoenixCommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    AppRouting,
    DxTextBoxModule,
    DxNumberBoxModule,
    DxDateBoxModule,
    DxSelectBoxModule,
    DxRadioGroupModule,
    DxTreeViewModule,
    RestrictionModule,
    ClientSpecificFieldsModule,
    MsalModule,
    LayoutModule,
    MatIconModule,
    NgxJsonViewerModule,
    MatButtonModule,
    MessageComponentsModule
  ],
  providers: [
    LoadingSpinnerService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    MessageComponentsModule.providerFactory(environment.messageApiEndpoint, [CommonService], (s: CommonService) => s.bearerToken(), ClutterlessTheme),
    { provide: AppStorageService, useFactory: (iframeService: IframeService) => appStorageServiceFactory(iframeService, localStorageFactory), deps: [IframeService] },
    { provide: RollbarService, useFactory: rollbarFactory, deps: [AppStorageService] },
    { provide: AppSessionStorageService, useFactory: (iframeService: IframeService) => appStorageServiceFactory(iframeService, sessionStorageFactory), deps: [IframeService] },
    CommonListsObservableService,
    UrlData,
    CommonDataService,
    CommonService,
    NavigationService,
    SignalrService,
    ApiService,
    AuthService,
    AccountService,
    EventService,
    RootService,
    IframeService,
    { provide: HTTP_INTERCEPTORS, useClass: LocaleInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    // msal
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ConfigurationService,
    UserBehaviorService,
    NumberToOrdinalPipe,
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'CAD'
    },
    provideAppInitializer(() => {
      const initializerFn = appInitializer();
      return initializerFn();
    }),
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: matFormFieldDefaultOptions },
    { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: matButtonToggleGroupDefaultOptions },
    provideLottieOptions({
      player: () => import('lottie-web/build/player/lottie_svg')
    }),
    provideHttpClient(withInterceptorsFromDi())
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
  constructor(rootService: RootService, httpClient: HttpClient) {
    config({ licenseKey: environment.devextremeKey });
    devextremeAjax.inject({ sendRequest: sendRequestFactory(httpClient) });
    rootService.init();
  }
}
